import type {
  ProfessionalCardRequestApprovalBlocker,
  ProfessionalCardRequestSchema,
  ProfessionalCardRequestStatusChange,
} from "@asap/shared/schemas/professional-card-request";
import type { TableRowAction } from "~/components/shared/table/TableRowActions.vue";
import { useProfessionalCardRequestApi } from "~/components/professional-card-request/composables/useProfessionalCardRequestApi";
import { useProfessionalCardResolutionFlags } from "~/components/professional-card-compliance/composables/useProfessionalCardResolutionFlags";
import {
  canApproveProfessionalCardRequest,
  canMarkProfessionalCardRequestAsPending,
  canRefuseProfessionalCardRequest,
  isProfessionalCardRequestApprovable,
} from "~/components/professional-card-request/professional-card-request.model";
import { useFeatureFlag } from "~/composables/useFeatureFlag";
import { useToast } from "~/composables/useToast";

/**
 * The actions a CSM takes on a carte BTP request, the same in the list and on the detail page: approve, refuse, cancel,
 * put back to pending, and copy its link. Each place decides how it shows them.
 */
export const useProfessionalCardRequestActions = ({
  onRequestUpdated,
}: {
  onRequestUpdated: (request: ProfessionalCardRequestSchema) => void;
}) => {
  const { t } = useI18n();
  const config = useRuntimeConfig();
  const { updateProfessionalCardRequestStatus } = useProfessionalCardRequestApi();
  const { addToastError, addToastSuccess } = useToast();
  const { copy } = useClipboard({ legacy: true });
  const { isFeatureFlagEnabled } = useFeatureFlag();
  const isDetailsV2Enabled = isFeatureFlagEnabled("professional-card-request-details-v2");
  const { isCardCreationResolutionEnabled } = useProfessionalCardResolutionFlags();

  // One update at a time per request: the list shares this across its rows.
  const requestIdsInFlight = new Set<string>();

  const approvalBlockerDescriptions = computed(
    () =>
      ({
        missing_card: t("professionalCardRequests.completeness.approveRequiresCard"),
      }) satisfies Record<ProfessionalCardRequestApprovalBlocker, string>
  );

  // Behind `professional-card-request-details-v2`, what the API says the request lacks; before it, the photo.
  const getApproveAvailability = (
    request: Pick<ProfessionalCardRequestSchema, "status" | "approval_blocker" | "completeness">
  ) => {
    if (isDetailsV2Enabled.value) {
      const { approval_blocker } = request;
      return {
        disabled: !isProfessionalCardRequestApprovable(request),
        description: approval_blocker ? approvalBlockerDescriptions.value[approval_blocker] : undefined,
      };
    }
    return {
      disabled: isCardCreationResolutionEnabled.value && !canApproveProfessionalCardRequest(request),
      description: isCardCreationResolutionEnabled.value
        ? t("professionalCardRequests.completeness.approveRequiresPhoto")
        : undefined,
    };
  };

  const updateRequestStatus = async (id: string, status: ProfessionalCardRequestStatusChange) => {
    if (requestIdsInFlight.has(id)) return;
    requestIdsInFlight.add(id);
    try {
      onRequestUpdated(await updateProfessionalCardRequestStatus(id, status));
      addToastSuccess({ title: t("professionalCardRequests.rowActions.updated") });
    } catch (error) {
      addToastError({ title: t("professionalCardRequests.rowActions.updateError") }, error);
    } finally {
      requestIdsInFlight.delete(id);
    }
  };

  const copyRequestLink = async (id: string) => {
    const url = new URL(config.public.FRONT_URL);
    url.pathname = `/professional-card-requests/${id}`;
    try {
      await copy(url.toString());
      addToastSuccess({ title: t("professionalCardRequests.details.linkCopied") });
    } catch (error) {
      addToastError({ title: t("professionalCardRequests.details.linkCopyError") }, error);
    }
  };

  // Approve and cancel treat a pending request, put back to pending reopens a treated one.
  const getTreatmentActions = (
    request: Pick<ProfessionalCardRequestSchema, "id" | "status" | "approval_blocker" | "completeness">
  ): TableRowAction[] => {
    if (request.status === "pending") {
      return [
        {
          label: t("professionalCardRequests.rowActions.approve"),
          icon: "i-ph-check-circle",
          ...getApproveAvailability(request),
          onClick: () => updateRequestStatus(request.id, "approved"),
        },
        {
          label: t("professionalCardRequests.rowActions.cancel"),
          icon: "i-ph-x-circle",
          onClick: () => updateRequestStatus(request.id, "canceled"),
        },
      ];
    }
    if (canMarkProfessionalCardRequestAsPending(request)) {
      return [
        {
          label: t("professionalCardRequests.rowActions.markAsPending"),
          icon: "i-ph-arrows-counter-clockwise",
          onClick: () => updateRequestStatus(request.id, "pending"),
        },
      ];
    }
    return [];
  };

  // The refusal opens the place's own modal.
  const getRefuseAction = (
    request: Pick<ProfessionalCardRequestSchema, "status">,
    refuse: () => void
  ): TableRowAction | undefined =>
    canRefuseProfessionalCardRequest(request)
      ? {
          label: t("professionalCardRequests.refusal.confirm"),
          icon: "i-ph-x-circle",
          color: "critical",
          onClick: refuse,
        }
      : undefined;

  const getCopyLinkAction = (request: Pick<ProfessionalCardRequestSchema, "id">): TableRowAction => ({
    label: t("professionalCardRequests.details.copyLink"),
    icon: "i-ph-link",
    onClick: () => copyRequestLink(request.id),
  });

  return { getTreatmentActions, getRefuseAction, getCopyLinkAction, copyRequestLink };
};
